
import { map } from 'rxjs/operators';
import { Injectable, OnInit } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { PermitFee as PaymentInfo, PermitFee } from "../models/permitFee.model";
import { PaymentItem } from "../models/paymentItem.model";
import { ApplicationPaymentParameters } from "../../applications/models/applications.models";

@Injectable()
export class PaymentCalculatorService {

    contextData: PaymentCalculatorConfigData;
    paymentInfos: PaymentInfo[] = [];
    templateId: string;
    workflowId: string;

    readonly BaseUrl = "/api/PermitFees/";

    constructor(public readonly httpClient: HttpClient) { }

    calculate(contextData: PaymentCalculatorConfigData, workflowId: string, templateId: string, acceptExpedites: boolean = true): Observable<any> {

        this.contextData = contextData;
        this.workflowId = workflowId;
        this.templateId = templateId;

        let calculatedFees: PaymentItem[] = new Array();

		return this.httpClient.get(this.BaseUrl).pipe(map(response => {
            this.paymentInfos = response as PaymentInfo[];
            if (templateId.startsWith('nvE7GRWnvEcdPo1Xic')) {  // Bonding                            
                this.calculateBondingFees(calculatedFees);
            } else if (templateId.startsWith('nLF6ry5nLF6M722uxu') || templateId.startsWith('nUPak0BnUPjgiB0CPw')) { // Well Transfer
                this.calculateWellTransferFees(calculatedFees);
            } else if (templateId.startsWith('nABkOa5nABkQ~I25Nm')) { // Temporary Inactive
                this.calculateTempInactive(calculatedFees);
            } else if (templateId.startsWith('nxJe7mqnxOxhi~2Noi')) { // Plug and Abandon
                this.calculatePlugAndAbandon(calculatedFees, acceptExpedites);
            } else if (templateId.startsWith('5r2NOW')) { // New Ops
                this.calculateNewOpsPermitFees(calculatedFees, acceptExpedites);
            } else { // General Permits
                this.calculatePermitFees(calculatedFees, templateId, acceptExpedites);
            }
			calculatedFees = calculatedFees.filter(f => f.fee > 0); //remove any fees which are currently set at $0
            return calculatedFees;
        }));
    }

    calculateBondingFees(calculatedFees: any[]): void {
        if (this.contextData.BNDcashInLieuAmount) {
            const feeInfo = this.paymentInfos.find(x => x.id === "01RFy8");
            if (feeInfo) {
                let types = this.contextData.suretyTypeField;
                let feeDescription = "Cash in Lieu of Surety Bond";
                if (types != null) {
                    let stratBond = types.find(t => t == 'Cash Bond for Stratigraphic Well');
                    if (stratBond != null) feeDescription = stratBond;
                }
                
                calculatedFees.push(
                    new PaymentItem(
                        "",
                        feeDescription,
                        feeInfo.legalReference,
                        Number(this.contextData.BNDcashInLieuAmount),
                        feeInfo.department,
                        feeInfo.fund,
                        feeInfo.ali,
                        feeInfo.program,
                        feeInfo.revenueSourceCode,
                        feeInfo.reporting,
                        feeInfo.accountDescription,
                        this.workflowId
                    ));
            }
        }

        if (this.contextData.BNDoneTimeFeeAmount) {
            const feeInfo = this.paymentInfos.find(x => x.id === "OikUj5");
            if (feeInfo) {
                calculatedFees.push(
                    new PaymentItem(
                        "",
                        'Exempt Domestic Well Fee',
                        feeInfo.legalReference,
                        Number(this.contextData.BNDoneTimeFeeAmount),
                        feeInfo.department,
                        feeInfo.fund,
                        feeInfo.ali,
                        feeInfo.program,
                        feeInfo.revenueSourceCode,
                        feeInfo.reporting,
                        feeInfo.accountDescription,
                        this.workflowId
                    ));
            }
        }
    }

    calculateBrineFees(calculatedFees: any[]): void {
        const brineFluidRecord = this.paymentInfos.find(x => x.id.toLowerCase() === "fluidproposed-brine");
        calculatedFees.push(
            new PaymentItem(
                "",
                brineFluidRecord.feeTypes[0],
                brineFluidRecord.legalReference,
                Number(brineFluidRecord.flatFee),
                brineFluidRecord.department,
                brineFluidRecord.fund,
                brineFluidRecord.ali,
                brineFluidRecord.program,
                brineFluidRecord.revenueSourceCode,
                brineFluidRecord.reporting,
                brineFluidRecord.accountDescription,
                this.workflowId
            )
        );
        // BRINE DISPOSAL FEES
        // THIS WILL NEED MORE WORK!!!!!!!!
        // Maximum/Cap amount according to spreadsheet
        const multiplierRecord = this.paymentInfos.find(x => x.id.toLowerCase() === "propdijvmax-disposal");
        if (multiplierRecord && this.contextData.propDIJVmax != null && this.contextData.propDIJVmax !== "" ) {
            let propDIJVmax = Number(this.contextData.propDIJVmax);
            if (propDIJVmax >= 500000)
                propDIJVmax = 50000;
            let bblFee = propDIJVmax * Number(multiplierRecord.flatFee);
            calculatedFees.push(
                new PaymentItem(
                    "",
                    'Brine Disposal Fees',
                    multiplierRecord.legalReference,
                    Number(bblFee),
                    multiplierRecord.department,
                    multiplierRecord.fund,
                    multiplierRecord.ali,
                    multiplierRecord.program,
                    multiplierRecord.revenueSourceCode,
                    multiplierRecord.reporting,
                    multiplierRecord.accountDescription,
                    this.workflowId
                )
            );
        }
    }

	calculateExpediteFees(calculatedFees: any[], feeInfo: any, allowExpedite: boolean): void {
		if (allowExpedite) {
			calculatedFees.push(new PaymentItem(
				"",
				'Expedite Fee',
				feeInfo.legalReference,
				feeInfo.expediteFee,
				feeInfo.department,
				feeInfo.fund,
				feeInfo.ali,
				feeInfo.program,
				feeInfo.revenueSourceCode,
				feeInfo.reporting,
				`Expedite Fee (${feeInfo.accountDescription})`,
				this.workflowId
			));
		}        
    }

    calculateNewOrExistingFees(calculatedFees: any[], newFeeRecord: any, description: string, urbanArea: string = ""): void {
        if (urbanArea.length == 0) urbanArea = this.contextData.urbanArea.toLowerCase();
        const feeString =
            newFeeRecord.feeMatrix.find(x => x.toLowerCase()
                .startsWith(urbanArea));

        if (feeString) {
            calculatedFees.push(
                new PaymentItem(
                    "",
                    description,
                    newFeeRecord.legalReference,
                    Number(feeString.split(":")[1]),
                    newFeeRecord.department,
                    newFeeRecord.fund,
                    newFeeRecord.ali,
                    newFeeRecord.program,
                    newFeeRecord.revenueSourceCode,
                    newFeeRecord.reporting,
                    newFeeRecord.accountDescription,
                    this.workflowId
                ));
        }
    }

    calculateMandatoryPoolingFees(calculatedFees: any[]): void {
        const mandatoryPoolingRecord = this.paymentInfos.find(x => x.id.toLowerCase() === "unitmpn-mandatorypooling");
        if (mandatoryPoolingRecord) {
            calculatedFees.push(
                new PaymentItem(
                    "",
                    'Mandatory Pooling',
                    mandatoryPoolingRecord.legalReference,
                    Number(mandatoryPoolingRecord.flatFee),
                    mandatoryPoolingRecord.department,
                    mandatoryPoolingRecord.fund,
                    mandatoryPoolingRecord.ali,
                    mandatoryPoolingRecord.program,
                    mandatoryPoolingRecord.revenueSourceCode,
                    mandatoryPoolingRecord.reporting,
                    mandatoryPoolingRecord.accountDescription,
                    this.workflowId
                ));
        }
    }

	calculatePermitFees(calculatedFees: any[], templateId: string, allowExpedite: boolean): void {

        let newOrExisting: string = this.contextData.newOrExisting ? this.contextData.newOrExisting.toLowerCase() : '';
        let wellType: string = this.contextData.WellType ? this.contextData.WellType.toLowerCase() : '';
        let wellTypeDescription: string = this.contextData.WellTypeDescription ? this.contextData.WellTypeDescription.toLowerCase() : '';
        let fluidProposed: string = this.contextData.fluidProposed ? this.contextData.fluidProposed.toLowerCase() : '';
        let expeditePermit: string = this.contextData.expeditePermit ? this.contextData.expeditePermit.toLowerCase() : '';        
        let unitMPn: string = this.contextData.unitMPn ? this.contextData.unitMPn.toLowerCase() : '';


        if (wellType == "sw_r" && wellTypeDescription == "") {
            this.calculateSaltWaterWellFees(calculatedFees);

            if (fluidProposed === "brine")
                this.calculateBrineFees(calculatedFees);            
        }

        // NEW wells, OR EXISTING - for Permit to Drill - which seems to have the same fees
        // BUT saltwater injection doesn't get the basic New Well fee
        if ((newOrExisting === "new" || templateId.startsWith('nxc~8T~nxc~fpJ0cFz')) && !(wellType == "sw_r" && wellTypeDescription == "")) {
            const newFeeRecord = this.paymentInfos.find(x => x.id.toLowerCase() === "neworexisting-new");
            if (newFeeRecord)
                this.calculateNewOrExistingFees(calculatedFees, newFeeRecord, "New Well");            

            if (expeditePermit  === "y" && newFeeRecord.expediteFee > 0 )
                this.calculateExpediteFees(calculatedFees, newFeeRecord, allowExpedite);            

            //TODO: revamp it so we're not trying to string match whatever is configged in the flow.
            if (unitMPn === "Will require a Mandatory Pooling Order (ORC 1509.27)".toLowerCase())
                this.calculateMandatoryPoolingFees(calculatedFees);            
        }
    }

    calculatePlatFees(): Observable<PaymentItem[]> {
		return this.httpClient.get(this.BaseUrl).pipe(map((feeData: PaymentInfo[]) => {
            this.paymentInfos = feeData;
            const platFee = this.paymentInfos.find(x => x.id.toLowerCase() === "pwsx75");
            let fees: PaymentItem[] = [];
            fees.push(
                new PaymentItem(
                    "",
                    platFee.feeTypes[0],
                    platFee.legalReference,
                    Number(platFee.flatFee),
                    platFee.department,
                    platFee.fund,
                    platFee.ali,
                    platFee.program,
                    platFee.revenueSourceCode,
                    platFee.reporting,
                    platFee.accountDescription,
                    this.workflowId
                )
            );
            return fees;
        }))
        
    }

    calculatePlugAndAbandon(calculatedFees: any[], allowExpedite: boolean): void {
        const feeInfo = this.paymentInfos.find(x => x.id === "1StB9h");
        if (feeInfo) {
            calculatedFees.push(
                new PaymentItem(
                    "",
                    "Plug & Abandon",
                    feeInfo.legalReference,
                    Number(feeInfo.flatFee),
                    feeInfo.department,
                    feeInfo.fund,
                    feeInfo.ali,
                    feeInfo.program,
                    feeInfo.revenueSourceCode,
                    feeInfo.reporting,
                    feeInfo.accountDescription,
                    this.workflowId
                ));

            let expeditePermit: string = this.contextData.expeditePermit ? this.contextData.expeditePermit.toLowerCase() : '';
			if (expeditePermit === "y" && feeInfo.expediteFee > 0) {
				this.calculateExpediteFees(calculatedFees, feeInfo, allowExpedite);
			}
                
        }
    }

	calculateNewOpsPermitFees(calculatedFees: any[], allowExpedite: boolean): void {
        let wellType: string = this.contextData.WellType ? this.contextData.WellType.toLowerCase() : '';
        let wellTypeDescription: string = this.contextData.WellTypeDescription ? this.contextData.WellTypeDescription.toLowerCase() : '';
        let fluidProposed: string = this.contextData.fluidProposed ? this.contextData.fluidProposed.toLowerCase() : '';

        //If they configure it to be a true false checkbox, handle that too.
        let expeditePermitValue = this.contextData.expeditePermit;
        if (this.contextData.expeditePermit) {
            if (this.contextData.expeditePermit === true)
                expeditePermitValue = "y";
            if (this.contextData.expeditePermit === false)
                expeditePermitValue = "n";
        }

        let expeditePermit: string = expeditePermitValue ? expeditePermitValue.toLowerCase() : '';
        let unitMPn: string = this.contextData.unitMPn ? this.contextData.unitMPn.toLowerCase() : '';
        let permitOperationType: string = this.contextData.newOpsPermitType ? this.contextData.newOpsPermitType : '';

        //Saltwater Class II Disposal is always a flat fee 
        if (wellType == "sw_r" && wellTypeDescription == "")
        {
            this.calculateSaltWaterWellFees(calculatedFees);

            if (fluidProposed === "brine")
                this.calculateBrineFees(calculatedFees);
        }
        else
        {
            //Nothing in their documentation says it shouldn't apply to all new ops permits, will change this if they say otherwise.
            //TODO: revamp it so we're not trying to string match whatever is configged in the flow.
            //Mandatory pooling in new ops only applies to Oil and Gas Wells
            if (unitMPn === "Will require a Mandatory Pooling Order (ORC 1509.27)".toLowerCase() && wellType == "og_r")
                this.calculateMandatoryPoolingFees(calculatedFees);

            if (permitOperationType) {
                switch (permitOperationType.toLowerCase()) {
                    case "reissue a previously issued permit":
                    case "permit to revise location": {
                        const reviseOrReissueFeeRecord = this.paymentInfos.find(x => x.id.toLowerCase() === "revisionorreissuance");
                        if (reviseOrReissueFeeRecord) {

                            if (expeditePermit === "y" && reviseOrReissueFeeRecord.expediteFee > 0)
                                this.calculateExpediteFees(calculatedFees, reviseOrReissueFeeRecord, allowExpedite);

                            this.calculateReviseOrReIssueFees(calculatedFees, reviseOrReissueFeeRecord, permitOperationType);
                        }
                        break;
                    }
                    default: {
                        //Other than Reissue, everything else seems to be the same for the rest, as well as permit to drill:
                        const newFeeRecord = this.paymentInfos.find(x => x.id.toLowerCase() === "neworexisting-new");
                        if (newFeeRecord) {
                            if (expeditePermit === "y" && newFeeRecord.expediteFee > 0)
                                this.calculateExpediteFees(calculatedFees, newFeeRecord, allowExpedite);

                            this.calculateNewOrExistingFees(calculatedFees, newFeeRecord, permitOperationType);
                        }
                        break;
                    };
                }
            }
        }
    }

    calculateSaltWaterWellFees(calculatedFees: any[]): void {
        const wellTypeRecord = this.paymentInfos.find(x => x.id.toLowerCase() === "welltype-sltwtrinj");
        if (wellTypeRecord) {
            calculatedFees.push(
                new PaymentItem(
                    "",
                    wellTypeRecord.feeTypes[0],
                    wellTypeRecord.legalReference,
                    Number(wellTypeRecord.flatFee),
                    wellTypeRecord.department,
                    wellTypeRecord.fund,
                    wellTypeRecord.ali,
                    wellTypeRecord.program,
                    wellTypeRecord.revenueSourceCode,
                    wellTypeRecord.reporting,
                    wellTypeRecord.accountDescription,
                    this.workflowId
                )
            );
        }
    }

    calculateStratigraphicWellFees(params: ApplicationPaymentParameters, allowExpedite: boolean): Observable<PaymentItem[]> {
        return this.httpClient.get(this.BaseUrl).pipe(map((feeData: PaymentInfo[]) => {
            let fees = this._calculateFees(params, feeData, allowExpedite);
            fees.forEach(f => {
                if (f.accountDescription.toLocaleLowerCase().includes("oil & gas")) {
                    f.accountDescription = f.accountDescription.replace("Oil & Gas", "Stratigraphic");
                }
            });
            return fees;
        }));
    }

    calculateTempInactive(calculatedFees: any[]): void {
        const feeInfo = this.paymentInfos.find(x => x.id === "Ebt6C1");
        if (feeInfo) {
            calculatedFees.push(
                new PaymentItem(
                    "",
                    "Temporary Inactive Status",
                    feeInfo.legalReference,
                    Number(feeInfo.flatFee),
                    feeInfo.department,
                    feeInfo.fund,
                    feeInfo.ali,
                    feeInfo.program,
                    feeInfo.revenueSourceCode,
                    feeInfo.reporting,
                    feeInfo.accountDescription,
                    this.workflowId
                ));
        }
    }

    calculateWellTransferFees(calculatedFees: any[]): void {
        //Check if they've provided a sale date, and if so if it was prior to the date at which a fee is specified as being required
        let saleDateRequiresFee = true;
        if (this.contextData.wellTransferSaleDate) {
            try {
                let parsedSaleDate = new Date(this.contextData.wellTransferSaleDate);
                let datePriortoWhichFeeIsAssessed = new Date(this.contextData.datePriortoWhichFeeIsAssessed);
                saleDateRequiresFee = parsedSaleDate < datePriortoWhichFeeIsAssessed;
            }
            catch{
            }
        }
        if (this.contextData.wellTransferAmount
            && saleDateRequiresFee) {
            const feeInfo = this.paymentInfos.find(x => x.id === "OgthH9");
            if (feeInfo) {
                calculatedFees.push(
                    new PaymentItem(
                        "",
                        "$100 Per Well Transferred",
                        feeInfo.legalReference,
                        Number(this.contextData.wellTransferAmount),
                        feeInfo.department,
                        feeInfo.fund,
                        feeInfo.ali,
                        feeInfo.program,
                        feeInfo.revenueSourceCode,
                        feeInfo.reporting,
                        feeInfo.accountDescription,
                        this.workflowId
                    ));
            }
        }
    }

    calculateUnitizationFees(calculatedFees: any[]): void {
        const unitizationRecord = this.paymentInfos.find(x => x.id.toLowerCase() === "unitmpn-unitizationchiefsorder");
        if (unitizationRecord) {
            calculatedFees.push(
                new PaymentItem(
                    "",
                    'Unitization',
                    unitizationRecord.legalReference,
                    Number(unitizationRecord.flatFee),
                    unitizationRecord.department,
                    unitizationRecord.fund,
                    unitizationRecord.ali,
                    unitizationRecord.program,
                    unitizationRecord.revenueSourceCode,
                    unitizationRecord.reporting,
                    unitizationRecord.accountDescription,
                    this.workflowId
                ));
        }
    }

    calculateReviseOrReIssueFees(calculatedFees: any[], feeInfo: PermitFee, description: string): void {
        calculatedFees.push(new PaymentItem(
            "",
            description,
            feeInfo.legalReference,
            Number(feeInfo.flatFee),
            feeInfo.department,
            feeInfo.fund,
            feeInfo.ali,
            feeInfo.program,
            feeInfo.revenueSourceCode,
            feeInfo.reporting,
            feeInfo.accountDescription,
            this.workflowId
        ));
    }

    private _calculateFees(params: ApplicationPaymentParameters, feeStructures: PaymentInfo[], allowExpedite: boolean): PaymentItem[] {
        let items: PaymentItem[] = [];
        if (params.permitType == null) return items;
        if (params.permitType == 'New Well') {
            const newFeeRecord = feeStructures.find(x => x.id.toLowerCase() === "neworexisting-new");
            if (newFeeRecord != null) {
                if (params.expeditePermit && newFeeRecord.expediteFee > 0) {
                    this.calculateExpediteFees(items, newFeeRecord, allowExpedite);
                }
                this.calculateNewOrExistingFees(items, newFeeRecord, "New Well", params.urbanArea.toLocaleLowerCase());
            }
        } else if (params.permitType.toLocaleLowerCase().includes("revise") || params.permitType.toLocaleLowerCase().includes("reissue")) {
            const reviseOrReissueFeeRecord = feeStructures.find(x => x.id.toLowerCase() === "revisionorreissuance");
            if (reviseOrReissueFeeRecord != null) {
                if (params.expeditePermit && reviseOrReissueFeeRecord.expediteFee > 0) {
                    this.calculateExpediteFees(items, reviseOrReissueFeeRecord, allowExpedite);
                }
                this.calculateReviseOrReIssueFees(items, reviseOrReissueFeeRecord, params.permitType);
            }
        } else {
            const newFeeRecord = feeStructures.find(x => x.id.toLowerCase() === "neworexisting-new");
            if (newFeeRecord) {
                if (params.expeditePermit && newFeeRecord.expediteFee > 0)
                    this.calculateExpediteFees(items, newFeeRecord, allowExpedite);

                this.calculateNewOrExistingFees(items, newFeeRecord, params.permitType, params.urbanArea.toLocaleLowerCase());
            }
        }

        return items;
    }
}

export interface PaymentCalculatorConfigData {    
    newOrExisting?: string,
    urbanArea?: string,
    WellType?: string,
    WellTypeDescription?: string,
    fluidProposed?: string,
    propDIJVmax?: string,
    unitMPn?: string,
    expeditePermit?: any,
    wellTransferAmount?: string,
    datePriortoWhichFeeIsAssessed?: string,
    wellTransferSaleDate?: string,
    BNDcashInLieuAmount?: string,
    BNDoneTimeFeeAmount: string,
    newOpsPermitType: string,
    suretyTypeField?: []
}